/**
 * Milestone Set Mission sub-KPI type.
 *
 * N of M discrete milestones complete. Score = completed / total × 100.
 *
 * Examples: 4 of 7 community partners signed; 3 of 5 inspections passed;
 * 8 of 10 curriculum modules delivered; 2 of 4 site approvals obtained.
 *
 * Distinguished from Count: Milestones are DISCRETE named items with a
 * defined list (curriculum modules, inspections, approvals). Count is a
 * continuous accumulating number (trees planted, students enrolled).
 *
 * Scoring math:
 *   score = round(completed / total × 100)
 *
 * Data shape: each MissionDataPoint's `value` is interpreted as the
 * cumulative count of completed milestones at that date. The configured
 * `milestones` list defines the total and the named items.
 *
 * Trend uses direction-aware delta with goal_direction='increase'.
 * Deadline degradation is configurable per-instance and supports a
 * `required_completion_at_deadline` percentage (e.g., 100% by deadline).
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2.
 */

import type {
  ConfigFieldSchema,
  MissionDataPoint,
  MissionSubKpiType,
  SetupExample,
  SubKpiScore,
  UiHints,
  ValidationError, // DEV-963: surfaced after TS 5.4 upgrade.
} from './types';
import { latestAsOf } from '../timeSeries';
import { applyDeadlineDegradation } from './deadline';
import { KPI_DEFAULTS } from '../defaults';
import { validateCommonCrossFields } from './commonValidations';

/**
 * A single milestone within a Milestone Set.
 */
export interface MilestoneItem {
  /** Stable identifier (GUID or slug). */
  id: string;
  /** User-facing label (e.g., "EPA notification of intent filed"). */
  label: string;
  /** Optional weight (default 1.0 — equal weighting across milestones). */
  weight?: number;
}

/**
 * Configuration for a Milestone Set sub-KPI instance.
 */
export interface MilestoneSetConfig extends Record<string, unknown> {
  /** Display name for the set (e.g., "Site Approvals Obtained"). */
  display_name: string;
  /** The milestones in this set. Order is meaningful for display. */
  milestones: MilestoneItem[];
  /** Optional deadline (YYYY-MM-DD) by which the required completion is expected. */
  deadline_date?: string;
  /** Required completion percentage at deadline (default 100). */
  required_completion_pct?: number;
  /** Days before deadline to trigger Yellow warning. Default 30. */
  warning_days?: number;
  /** Days before deadline to trigger Red critical. Default 7. */
  critical_days?: number;
  /** Whether deadline-proximity degradation is applied. Default true. */
  deadline_degradation_enabled?: boolean;
  /** Score >= this is green. Default 80. */
  green_threshold?: number;
  /** Score >= this (and < green_threshold) is yellow. Default 50. */
  yellow_threshold?: number;
}

/* ------------------------------------------------------------------------- */
/*  Scoring                                                                  */
/* ------------------------------------------------------------------------- */

function scoreMilestoneSet(
  config: MilestoneSetConfig,
  data: MissionDataPoint[],
  asOfDate: string,
): SubKpiScore {
  const total = config.milestones?.length ?? 0;

  if (total === 0) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: null,
      target_value: 0,
      baseline_value: 0,
      explain: 'No milestones configured — score not computed.',
    };
  }

  const pts = data.map((d) => ({ date: d.date, value: d.value }));
  const cur = latestAsOf(pts, asOfDate);

  if (!cur) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: null,
      target_value: total,
      baseline_value: 0,
      explain: 'No milestone completion data available as of this date.',
    };
  }

  const completed = Math.max(0, Math.min(total, Math.round(cur.value)));
  const completionPct = (completed / total) * 100;
  const baseScore = Math.round(completionPct);
  const requiredPct =
    config.required_completion_pct ??
    KPI_DEFAULTS.mission.milestone_set_required_completion_pct;
  const goalMet = completionPct >= requiredPct;

  const degraded = applyDeadlineDegradation({
    base_score: baseScore,
    goal_met: goalMet,
    asOfDate,
    config: {
      deadline_date: config.deadline_date,
      warning_days: config.warning_days,
      critical_days: config.critical_days,
      deadline_degradation_enabled: config.deadline_degradation_enabled,
    },
  });

  const baseExplain = `${completed} of ${total} milestones complete (${baseScore}%).`;

  return {
    score_0_100: degraded.score,
    // DEV-864: Raw progress = (completed / total) * 100.
    progress_0_100: baseScore,
    current_value: completed,
    target_value: total,
    baseline_value: 0,
    explain: degraded.explain ? `${degraded.explain} ${baseExplain}` : baseExplain,
  };
}

/* ------------------------------------------------------------------------- */
/*  Color derivation                                                         */
/* ------------------------------------------------------------------------- */

function colorFromScore(
  score: SubKpiScore,
  config: MilestoneSetConfig,
): 0 | 1 | 2 | 3 {
  if (score.score_0_100 === null) return 0;
  const greenAt = config.green_threshold ?? KPI_DEFAULTS.mission.milestone_set_bands.green_threshold;
  const yellowAt = config.yellow_threshold ?? KPI_DEFAULTS.mission.milestone_set_bands.yellow_threshold;
  if (score.score_0_100 >= greenAt) return 3;
  if (score.score_0_100 >= yellowAt) return 2;
  return 1;
}

/* ------------------------------------------------------------------------- */
/*  Validation                                                               */
/* ------------------------------------------------------------------------- */

function validateConfig(config: MilestoneSetConfig): ValidationError | null {
  // DEV-816 / Skip: per-field required checks live in the schema. Only
  // structural rules that the schema can't express belong here.
  // Milestones with blank labels are a structural integrity issue (a
  // milestone needs a name to be referenced in updates) — pin to the
  // milestones field so the editor highlights it.
  if (Array.isArray(config.milestones) && config.milestones.length > 0) {
    for (const m of config.milestones) {
      if (!m.label || String(m.label).trim() === '') {
        return { field: 'milestones', message: 'Each milestone needs a non-empty label.' };
      }
    }
  }
  if (
    config.required_completion_pct !== undefined &&
    Number.isFinite(config.required_completion_pct) &&
    (config.required_completion_pct < 0 || config.required_completion_pct > 100)
  ) {
    return {
      field: 'required_completion_pct',
      message: 'Required completion percentage must be between 0 and 100.',
    };
  }
  return validateCommonCrossFields(config);
}

/* ------------------------------------------------------------------------- */
/*  Schema, examples, and UI hints                                           */
/* ------------------------------------------------------------------------- */

const config_schema: ConfigFieldSchema[] = [
  {
    key: 'display_name',
    label: 'Display name for this checklist',
    type: 'string',
    required: true,
    help_text: 'A short name for the checklist (e.g., "Site Approvals Obtained").',
  },
  {
    key: 'milestones',
    label: 'Items',
    type: 'list',
    required: true,
    help_text: 'List the items that must be checked off. Add as many as needed.',
  },
  {
    key: 'tracking_start_date',
    label: 'Tracking start date (optional)',
    type: 'date',
    required: false,
    help_text: 'Scoring begins on this date. Reporting periods before this date are ignored.',
    inline_with_next: true, // pair with deadline_date (DEV-816)
  },
  {
    key: 'deadline_date',
    label: 'Target deadline (optional)',
    type: 'date',
    required: false,
    help_text: 'When the required completion percentage should be reached.',
  },
  {
    key: 'required_completion_pct',
    label: 'Required completion at deadline (%)',
    type: 'number',
    required: false,
    default_value: 100,
    help_text: 'What percentage of milestones must be complete by the deadline. Default 100.',
    validation: { min: 0, max: 100 },
  },
  // deadline_degradation_enabled removed (DEV-814 / Skip): redundant once
  // color_mode is explicit. See count.ts for the rationale.
  // warning_days / critical_days deliberately NOT exposed in the form (DEV-814).
  // They were a redundant second pair of red/yellow controls. Deadline
  // degradation still uses internal defaults (KPI_DEFAULTS.deadline) when
  // deadline_degradation_enabled is true. The user only configures the SCORE
  // thresholds below; deadline pressure caps the score into the right band.
  {
    key: 'color_mode',
    label: 'Color the indicator from',
    type: 'enum',
    required: false,
    default_value: 'score',
    enum_options: [
      {
        value: 'score',
        label: 'Completion (% of milestones done)',
        help: 'Color is driven by what percentage of the milestone list has been completed. Set the % thresholds below for green/yellow/red.',
      },
      {
        value: 'deadline',
        label: 'Time remaining until deadline',
        help: 'Color is driven by days remaining: green far from deadline, yellow within the warning window, red within the critical window. Useful when the deadline matters more than the count of milestones hit.',
      },
    ],
    help_text: 'Pick how the indicator color is computed.',
  },
  // Labels reframed for consistency (DEV-814 / Skip): see count.ts.
  // Milestone Set keeps its more lenient yellow band (50 vs 60 elsewhere)
  // since partial completion is meaningful progress for milestone lists.
  {
    key: 'green_threshold',
    label: 'Yellow when % of milestones complete drops below',
    type: 'number',
    required: false,
    default_value: 80,
    suffix: '%',
    help_text: 'Indicator turns yellow once the % of milestones complete drops below this number. Default 80.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
    inline_with_next: true, // pair with red threshold (DEV-816)
  },
  {
    key: 'yellow_threshold',
    label: 'Red when % of milestones complete drops below',
    type: 'number',
    required: false,
    default_value: 50,
    suffix: '%',
    help_text: 'Indicator turns red once the % of milestones complete drops below this number. Must be less than yellow. Default 50.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
  },
  // Deadline-mode windows reframed (DEV-816 / Skip) to "drops below" pattern.
  {
    key: 'warning_days',
    label: 'Yellow when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 30,
    suffix: 'days',
    help_text: 'Indicator turns yellow once fewer than this many days remain until the deadline. Default 30.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
    inline_with_next: true, // pair with critical_days (DEV-816)
  },
  {
    key: 'critical_days',
    label: 'Red when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 7,
    suffix: 'days',
    help_text: 'Indicator turns red once fewer than this many days remain until the deadline. Must be less than yellow. Default 7.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
  },
];

const setup_examples: SetupExample[] = [
  {
    title: 'Site approvals obtained',
    description:
      'Track the four discrete approvals required to commission a wastewater facility. ' +
      'All four required by deadline.',
    example_config: {
      display_name: 'Site Approvals Obtained',
      milestones: [
        { id: 'epa-noi', label: 'EPA notification of intent filed' },
        { id: 'state-approval', label: 'State approval received' },
        { id: 'local-board', label: 'Local board sign-off' },
        { id: 'final-permit', label: 'Final operating permit' },
      ],
      required_completion_pct: 100,
      deadline_date: '2026-12-31',
    },
  },
  {
    title: 'Curriculum modules delivered',
    description:
      'Track delivery of the 10 modules in the Wastewater Education program. ' +
      'At least 80% must complete by the end of the cohort.',
    example_config: {
      display_name: 'Curriculum Modules Delivered',
      milestones: [
        { id: 'm1', label: 'Module 1: Course intro' },
        { id: 'm2', label: 'Module 2: Wastewater history & Clean Water Act' },
        { id: 'm3', label: 'Module 3: Treatment processes' },
        { id: 'm4', label: 'Module 4: Collection & primary treatment' },
        { id: 'm5', label: 'Module 5: Secondary & tertiary treatment' },
        { id: 'm6', label: 'Module 6: Local utility tour' },
        { id: 'm7', label: 'Module 7: Advanced reference plant tour' },
        { id: 'm8', label: 'Module 8: Solution recommendations' },
        { id: 'm9', label: 'Module 9: Presentation prep' },
        { id: 'm10', label: 'Module 10: Final presentations' },
      ],
      required_completion_pct: 80,
      deadline_date: '2026-06-13',
    },
  },
  {
    title: 'Inspections passed',
    description:
      'Track the discrete safety and quality inspections required to operate. ' +
      'All inspections must pass.',
    example_config: {
      display_name: 'Inspections Passed',
      milestones: [
        { id: 'fire', label: 'Fire safety inspection' },
        { id: 'health', label: 'Health department inspection' },
        { id: 'building', label: 'Building code inspection' },
        { id: 'electrical', label: 'Electrical inspection' },
        { id: 'final', label: 'Final occupancy inspection' },
      ],
      required_completion_pct: 100,
    },
  },
];

const ui_hints: UiHints = {
  default_chart: 'progress',
  value_format: 'count_with_unit',
  show_target_line: false,
};

/* ------------------------------------------------------------------------- */
/*  Export                                                                   */
/* ------------------------------------------------------------------------- */

export const milestoneSetType: MissionSubKpiType<MilestoneSetConfig> = {
  // Internal type_key stays 'milestone_set' for DB / API stability — no
  // migration needed for the rename. Only display strings change.
  // (DEV-816 / Skip: rename rationale — items don't carry per-item dates,
  // so "Milestone Set" overpromises. This is a checklist. If real
  // milestone-with-date use cases emerge, add a separate Milestone type.)
  type_key: 'milestone_set',
  display_name: 'Checklist',
  description:
    'Track a list of named items that should each be marked complete by a deadline.',
  examples: [
    'site approvals',
    'regulatory filings',
    'training modules',
  ],
  use_when: 'Have we checked off the required items?',
  supported_color_modes: ['score', 'deadline'],
  goal_direction: 'increase',
  config_schema,
  validate_config: validateConfig,
  score: scoreMilestoneSet,
  color_from_score: colorFromScore,
  ui_hints,
  setup_examples,
};
